import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "../../lib/supabase";

// The QuickBooks page's connection card (/support/quickbooks): the single
// QuickBooks Online company the Stripe to QBO connector posts to (DEV-2436).
// Reads qbo_connector.connection_safe (never the base table, which holds the
// Vault secret id) and drives the qbo-oauth Edge Function for connect and
// disconnect. Connect says it is working from the click until the browser
// leaves for Intuit, because the start call and Intuit's page take seconds.

const SOON_DAYS = 14;
// Connect stops saying it is working when the browser has not left by then
// (a cancelled or blocked navigation), so the card never stays stuck.
const LEAVE_TIMEOUT_MS = 20000;
const DAY_MS = 86400000;

const BAD = { label: "Error", bg: "#FFF5F5", fg: "#B42318" };

const STATUS = {
  connected: { label: "Connected", bg: "#E6F6EC", fg: "#1B7F43" },
  expired: { label: "Expired", bg: "#FEF3C7", fg: "#92400E" },
  revoked: { ...BAD, label: "Revoked" },
  error: BAD,
  disconnected: { label: "Not connected", bg: "#F0F0F0", fg: "#626262" },
};

const REASONS = {
  access_denied: "Access was denied on the Intuit screen.",
  invalid_state: "The sign-in link expired. Try again.",
  missing_code: "Intuit did not return an authorization code. Try again.",
  missing_realm: "Intuit did not say which company was chosen. Try again.",
  exchange_failed: "Intuit rejected the authorization code. Try again.",
  invalid_token_response: "Intuit returned an unexpected response. Try again.",
  realm_mismatch: "Could not confirm the company with Intuit. Nothing was saved.",
  company_check_failed: "QuickBooks could not be reached to confirm the company. Try again.",
  store_failed: "The connection could not be saved. Try again.",
  company_has_postings: "The connector has records for the current QuickBooks company or environment, so it cannot switch. Ask engineering to clear them first. Nothing was saved.",
};
const GENERIC_REASON = "Something went wrong. Try again.";

function daysUntil(iso) {
  return Math.ceil((new Date(iso).getTime() - Date.now()) / DAY_MS);
}

function fmtDay(iso) {
  return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function fmtDateTime(iso) {
  return new Date(iso).toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

function inDays(n) {
  if (n < 0) return "already passed";
  return n === 1 ? "in 1 day" : `in ${n} days`;
}

const ENVIRONMENTS = { sandbox: "Sandbox", production: "Production" };

const defaultNavigate = (url) => window.location.assign(url);

const CARD_CSS = `
  @keyframes qboSpin { to { transform: rotate(360deg); } }
  @keyframes qboPulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.45; } }
  .qbo-spin { animation: qboSpin 0.8s linear infinite; }
  .qbo-pulse { animation: qboPulse 1.4s ease-in-out infinite; }
  .qbo-btn { transition: background-color 150ms ease-out, border-color 150ms ease-out, opacity 150ms ease-out; }
  .qbo-btn:focus-visible { outline: 2px solid #F90091; outline-offset: 2px; }
  .qbo-btn-primary:not(:disabled):hover { background: #D6007C; }
  .qbo-btn-ghost:not(:disabled):hover { border-color: #CFCFCF; color: #3E3E3E; }
  @media (prefers-reduced-motion: reduce) {
    .qbo-spin, .qbo-pulse { animation: none; }
    .qbo-btn { transition: none; }
  }
`;

const card = { background: "#FFFFFF", border: "1px solid #E2E2E2", borderRadius: 14, boxShadow: "0 3px 15px rgba(0,0,0,0.06)", padding: "20px 24px" };
const btnBase = { display: "inline-flex", alignItems: "center", gap: 8, padding: "9px 16px", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", flexShrink: 0, fontFamily: "inherit" };
const btnPrimary = { ...btnBase, background: "#F90091", color: "#fff", border: "1px solid #F90091" };
const btnGhost = { ...btnBase, background: "#FFFFFF", color: "#626262", border: "1px solid #E2E2E2", fontWeight: 500 };
const muted = { fontSize: 13, color: "#626262", margin: 0 };
const pill = { fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 20 };

function Spinner() {
  return (
    <svg className="qbo-spin" width="14" height="14" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <circle cx="8" cy="8" r="6.5" fill="none" stroke="currentColor" strokeOpacity="0.3" strokeWidth="2" />
      <path d="M8 1.5a6.5 6.5 0 0 1 6.5 6.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function Button({ variant, pending, disabled, children, ...rest }) {
  const look = variant === "primary" ? btnPrimary : btnGhost;
  const off = disabled || pending;
  return (
    <button
      type="button"
      className={`qbo-btn qbo-btn-${variant}`}
      disabled={off}
      aria-busy={pending || undefined}
      style={off ? { ...look, cursor: "default", opacity: pending ? 0.85 : 0.6 } : look}
      {...rest}
    >
      {pending && <Spinner />}
      {children}
    </button>
  );
}

function Detail({ term, children, tone }) {
  return (
    <>
      <dt style={{ fontSize: 13, color: "#626262" }}>{term}</dt>
      <dd style={{ margin: 0, fontSize: 13, color: tone ?? "#151515", fontVariantNumeric: "tabular-nums" }}>{children}</dd>
    </>
  );
}

export function QuickBooksConnectionCard({ navigate = defaultNavigate }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [row, setRow] = useState(null);
  const [loadState, setLoadState] = useState("loading"); // loading | error | ready
  const [pending, setPending] = useState(null); // null | "connect" | "disconnect"
  const [confirming, setConfirming] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [result, setResult] = useState(null); // { ok, reason }
  const [notice, setNotice] = useState(null);
  const leaveTimer = useRef(null);

  useEffect(() => () => clearTimeout(leaveTimer.current), []);

  const load = useCallback(async () => {
    if (!supabase) { setLoadState("error"); return; }
    const { data, error } = await supabase
      .schema("qbo_connector")
      .from("connection_safe")
      .select("status, environment, realm_id, last_refreshed_at, refresh_token_expires_at, refresh_token_hard_expires_at, last_error")
      .maybeSingle();
    setRow(error ? null : data);
    setLoadState(error ? "error" : "ready");
  }, []);

  useEffect(() => { load(); }, [load]);

  // Back from Intuit with the browser's Back button restores this page from the
  // back/forward cache with Connect still pending: offer it again.
  useEffect(() => {
    const onPageShow = (event) => { if (event.persisted) setPending(null); };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  // One-shot result of the OAuth round trip: show it once, then strip the params.
  useEffect(() => {
    if (searchParams.get("integration") !== "quickbooks") return;
    const ok = searchParams.get("status") === "connected";
    const reason = searchParams.get("reason");
    setNotice(null);
    setResult({ ok, reason: REASONS[reason] ?? GENERIC_REASON, company: searchParams.get("company") });
    const next = new URLSearchParams(searchParams);
    ["integration", "status", "reason", "company"].forEach((k) => next.delete(k));
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const connect = async () => {
    setNotice(null);
    setResult(null);
    setPending("connect");
    setActionError(null);
    const { data, error } = await supabase.functions.invoke("qbo-oauth", {
      // Only the origin is used: qbo-oauth owns the return path (/support/quickbooks).
      body: { action: "start", return_url: window.location.origin },
    });
    if (error || !data?.url) {
      setPending(null);
      setActionError("Could not start the QuickBooks connection. Try again.");
      return;
    }
    // Stays pending: the page is about to unload.
    navigate(data.url);
    leaveTimer.current = setTimeout(() => setPending(null), LEAVE_TIMEOUT_MS);
  };

  const disconnect = async () => {
    setNotice(null);
    setPending("disconnect");
    setActionError(null);
    const { data, error } = await supabase.functions.invoke("qbo-oauth", { body: { action: "disconnect" } });
    setPending(null);
    setConfirming(false);
    if (error) { setActionError("Could not disconnect QuickBooks. Try again."); return; }
    setResult(null);
    // Only Disconnect revokes at Intuit; if that did not happen, the app is still
    // authorized there and the user has to remove it.
    setNotice(data?.revoked === false ? "Disconnected here. Also remove the Audiohook app under Connected apps in QuickBooks." : null);
    await load();
  };

  const busy = pending !== null;
  const ready = loadState === "ready";
  const status = row?.status ?? "disconnected";
  const hasCompany = Boolean(row) && status !== "disconnected";
  const isConnected = status === "connected";
  const badge = STATUS[status];

  const rollingDays = hasCompany && row.refresh_token_expires_at ? daysUntil(row.refresh_token_expires_at) : null;
  const hardDays = hasCompany && row.refresh_token_hard_expires_at ? daysUntil(row.refresh_token_hard_expires_at) : null;
  const expiresSoon = isConnected && [rollingDays, hardDays].some((d) => d !== null && d <= SOON_DAYS);
  const soonTone = (d) => (isConnected && d !== null && d <= SOON_DAYS ? "#92400E" : undefined);

  return (
    <section aria-labelledby="qbo-connection-heading" aria-busy={loadState === "loading" || undefined} style={card}>
      <style>{CARD_CSS}</style>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <h2 id="qbo-connection-heading" style={{ fontSize: 16, fontWeight: 700, color: "#151515", margin: 0 }}>Connected company</h2>
          {ready && <span style={{ ...pill, background: badge.bg, color: badge.fg }}>{badge.label}</span>}
          {expiresSoon && <span style={{ ...pill, background: "#FEF3C7", color: "#92400E" }}>Expires soon</span>}
        </div>
        {ready && (
          <div style={{ display: "flex", gap: 8 }}>
            {!isConnected && (
              <Button variant="primary" onClick={connect} pending={pending === "connect"} disabled={busy}>
                {pending === "connect" ? "Opening QuickBooks…" : hasCompany ? "Reconnect QuickBooks" : "Connect QuickBooks"}
              </Button>
            )}
            {hasCompany && !confirming && (
              <Button variant="ghost" onClick={() => setConfirming(true)} disabled={busy}>Disconnect</Button>
            )}
          </div>
        )}
      </div>

      {pending === "connect" && (
        <p role="status" style={{ ...muted, marginTop: 12 }}>
          Taking you to Intuit to choose the company. This can take a few seconds.
        </p>
      )}

      {result && (
        <div role="status" style={{ marginTop: 14, padding: "10px 12px", borderRadius: 10, fontSize: 13, background: result.ok ? "#E6F6EC" : "#FFF5F5", border: `1px solid ${result.ok ? "#A7DDBB" : "#fca5a5"}`, color: result.ok ? "#1B7F43" : "#B42318" }}>
          {result.ok ? `QuickBooks connected${result.company ? `: ${result.company}` : ""}.` : <>Could not connect QuickBooks. {result.reason}</>}
        </div>
      )}

      {notice && (
        <div role="status" style={{ marginTop: 14, padding: "10px 12px", borderRadius: 10, fontSize: 13, background: "#FEF3C7", border: "1px solid #F5D77A", color: "#92400E" }}>{notice}</div>
      )}

      {loadState === "loading" && (
        <div style={{ marginTop: 16, display: "grid", gap: 10 }}>
          <span style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap" }}>Loading the QuickBooks connection</span>
          {[220, 300, 260].map((w) => (
            <div key={w} className="qbo-pulse" aria-hidden="true" style={{ height: 12, width: w, maxWidth: "100%", borderRadius: 6, background: "#F0F0F0" }} />
          ))}
        </div>
      )}
      {loadState === "error" && <p role="alert" style={{ ...muted, color: "#B42318", marginTop: 12 }}>Could not load the QuickBooks connection.</p>}

      {ready && !hasCompany && pending !== "connect" && (
        <p style={{ ...muted, marginTop: 12, maxWidth: "65ch" }}>
          Connect the one QuickBooks Online company the Stripe connector posts to. You choose the company on Intuit's site, then come back here.
        </p>
      )}

      {hasCompany && (
        <dl style={{ margin: "16px 0 0", display: "grid", gridTemplateColumns: "minmax(130px, max-content) 1fr", columnGap: 24, rowGap: 8 }}>
          <Detail term="Company ID">{row.realm_id}</Detail>
          <Detail term="Environment">{ENVIRONMENTS[row.environment] ?? row.environment}</Detail>
          {row.last_refreshed_at && <Detail term="Last refreshed">{fmtDateTime(row.last_refreshed_at)}</Detail>}
          {row.refresh_token_expires_at && (
            <Detail term="Refresh token expires" tone={soonTone(rollingDays)}>
              {fmtDay(row.refresh_token_expires_at)}, {inDays(rollingDays)}. Each refresh extends it.
            </Detail>
          )}
          {row.refresh_token_hard_expires_at && (
            <Detail term="Absolute limit" tone={soonTone(hardDays)}>
              {fmtDay(row.refresh_token_hard_expires_at)}, {inDays(hardDays)}. Reconnect before then.
            </Detail>
          )}
          {row.last_error && <Detail term="Last error" tone="#B42318">{row.last_error}</Detail>}
        </dl>
      )}

      {confirming && (
        <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid #F0F0F0", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: 13, color: "#151515", marginRight: 4 }}>Disconnect QuickBooks? Postings stop until it is connected again.</span>
          <Button variant="primary" onClick={disconnect} pending={pending === "disconnect"} disabled={busy}>
            {pending === "disconnect" ? "Disconnecting…" : "Confirm disconnect"}
          </Button>
          <Button variant="ghost" onClick={() => setConfirming(false)} disabled={busy}>Cancel</Button>
        </div>
      )}

      {actionError && <p role="alert" style={{ ...muted, color: "#B42318", marginTop: 12 }}>{actionError}</p>}
    </section>
  );
}
